<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TextArea from 'next/textarea/TextArea.vue';
import { useStore } from 'dashboard/composables/store';
import { useAgentsList } from 'dashboard/composables/useAgentsList';

const props = defineProps({
  inboxId: {
    type: Number,
    default: 0,
  },
  currentAssigneeId: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['submit']);

const store = useStore();
const { t } = useI18n();
const dialogRef = ref(null);
const isLoadingAgents = ref(false);
const state = reactive({
  agentId: null,
  note: '',
});

const { agentsList } = useAgentsList(false);

const agentOptions = computed(() =>
  agentsList.value.map(agent => ({
    value: agent.id,
    label: agent.name,
  }))
);

const hasAgents = computed(() => agentOptions.value.length > 0);
const normalizedCurrentAssigneeId = computed(() =>
  Number(props.currentAssigneeId) > 0 ? Number(props.currentAssigneeId) : null
);

const isFormValid = computed(() => {
  if (!hasAgents.value) return false;
  if (!state.agentId) return false;

  return Number(state.agentId) !== normalizedCurrentAssigneeId.value;
});

const close = () => {
  dialogRef.value?.close();
  state.agentId = null;
  state.note = '';
};

const open = async () => {
  state.agentId = normalizedCurrentAssigneeId.value;
  state.note = '';

  if (props.inboxId) {
    try {
      isLoadingAgents.value = true;
      await store.dispatch('inboxAssignableAgents/fetch', [props.inboxId]);
    } catch (error) {
      // Keep the dialog usable even if the agent fetch fails.
    } finally {
      isLoadingAgents.value = false;
    }
  }

  dialogRef.value?.open();
};

const handleConfirm = () => {
  if (!isFormValid.value) return;

  emit('submit', {
    agentId: Number(state.agentId),
    note: state.note?.trim() || '',
  });
  close();
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :title="t('CONVERSATION.TRANSFER_MODAL.TITLE')"
    :description="t('CONVERSATION.TRANSFER_MODAL.DESCRIPTION')"
    :confirm-button-label="t('CONVERSATION.TRANSFER_MODAL.CONFIRM')"
    :cancel-button-label="t('CONVERSATION.TRANSFER_MODAL.CANCEL')"
    :disable-confirm-button="!isFormValid || isLoadingAgents"
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-3">
      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('CONVERSATION.TRANSFER_MODAL.AGENT_LABEL') }}
        </label>
        <ComboBox
          v-model="state.agentId"
          :options="agentOptions"
          :placeholder="t('CONVERSATION.TRANSFER_MODAL.AGENT_PLACEHOLDER')"
          :disabled="isLoadingAgents || !hasAgents"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('CONVERSATION.TRANSFER_MODAL.NOTE_LABEL') }}
        </label>
        <TextArea
          v-model="state.note"
          class="w-full"
          :placeholder="t('CONVERSATION.TRANSFER_MODAL.NOTE_PLACEHOLDER')"
        />
      </div>

      <p
        v-if="!isLoadingAgents && !hasAgents"
        class="m-0 text-sm text-n-slate-11"
      >
        {{ t('CONVERSATION.TRANSFER_MODAL.EMPTY_STATE') }}
      </p>
    </div>
  </Dialog>
</template>
